/**
 * iOS-safe body-scroll lock with reference counting.
 *
 * `overflow: hidden` on `<body>` is unreliable on iOS Safari — touch
 * scroll still bleeds through. The canonical fix is to apply
 * `position: fixed; top: -<savedScrollY>px` so the viewport's scroll
 * container becomes the (now-unscrollable) viewport itself. On unlock
 * we restore the original styles AND scroll position so the page
 * looks unchanged.
 *
 * Ref-counted so multiple overlays (e.g. a floating drawer and a modal
 * opened on top of it) can request the lock independently — the body
 * stays locked until the last caller releases.
 *
 * Callers should pair every `lockBodyScroll()` with one
 * `unlockBodyScroll()`. Each component should track its own lock state
 * (a boolean) so disconnect / repeated calls don't desync the count.
 */

interface SavedBodyStyles {
  position: string
  top: string
  insetInlineStart: string
  insetInlineEnd: string
  inlineSize: string
  overflow: string
}

let lockCount = 0
let savedScrollY = 0
let savedBodyStyles: SavedBodyStyles | null = null

export function lockBodyScroll() {
  if (typeof document === 'undefined')
    return
  lockCount++
  if (lockCount > 1)
    return // already locked by an earlier caller

  savedScrollY = window.scrollY || window.pageYOffset || 0
  const body = document.body
  savedBodyStyles = {
    position: body.style.position,
    top: body.style.top,
    insetInlineStart: body.style.insetInlineStart,
    insetInlineEnd: body.style.insetInlineEnd,
    inlineSize: body.style.inlineSize,
    overflow: body.style.overflow,
  }
  // `position: fixed` + the inverse of the saved scroll keeps the
  // visible page in place. `inline-size: 100%` prevents the body from
  // collapsing to content width once it's out of flow. `overflow:
  // hidden` is the desktop / Android safety net.
  body.style.position = 'fixed'
  body.style.top = `-${savedScrollY}px`
  body.style.insetInlineStart = '0'
  body.style.insetInlineEnd = '0'
  body.style.inlineSize = '100%'
  body.style.overflow = 'hidden'
}

export function unlockBodyScroll() {
  if (typeof document === 'undefined')
    return
  if (lockCount === 0)
    return // nothing to unlock
  lockCount--
  if (lockCount > 0)
    return // still locked by another caller

  const body = document.body
  if (savedBodyStyles) {
    body.style.position = savedBodyStyles.position
    body.style.top = savedBodyStyles.top
    body.style.insetInlineStart = savedBodyStyles.insetInlineStart
    body.style.insetInlineEnd = savedBodyStyles.insetInlineEnd
    body.style.inlineSize = savedBodyStyles.inlineSize
    body.style.overflow = savedBodyStyles.overflow
    savedBodyStyles = null
  }
  // Restore scroll without smooth-scroll — instant snap so the user
  // doesn't see the page animate back to where they were.
  window.scrollTo(0, savedScrollY)
}
